// components/common/JsonEditor.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Flex, Input, Space, Tooltip, Typography, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import copy from "copy-to-clipboard";

// If you want schema validation, install ajv:
//   npm i ajv
// and then uncomment the import below.
// import Ajv, { ErrorObject, Options as AjvOptions } from "ajv";

const { TextArea } = Input;
const { Title, Text } = Typography;

export type JsonEditorProps<T = any> = {
    /** Title for the editor UI */
    title?: string;
    /** Initial object value (will be formatted to JSON). Ignored if `initialText` is provided. */
    value?: T;
    /** Initial raw JSON text (takes precedence over `value`). */
    initialText?: string;
    /** Optional sample object shown via "Load Sample" */
    sample?: T;
    /** Called when user clicks Apply and JSON is valid (parsed). */
    onApply?: (data: T) => void;
    /** Called every time text changes (raw JSON string). */
    onChangeText?: (raw: string) => void;
    /** Disable editing */
    readOnly?: boolean;
    /** Ant TextArea autoSize rows */
    minRows?: number;
    /** Constrain editor's max height (e.g. inside modals) */
    maxHeight?: number | string;
    /** Add extra buttons on the right of action bar */
    extraActions?: React.ReactNode;
    /** Persist current text in localStorage under this key */
    storageKey?: string;
    /** Show statement (énoncé) text area above JSON editor */
    showStatement?: boolean;
    /** Initial statement value */
    initialStatement?: string;
    /** Called when statement changes */
    onStatementChange?: (statement: string) => void;

    // JSON Schema validation (optional)
    // schema?: object;
    // ajvOptions?: AjvOptions;
};

export default function JsonEditor<T = any>({
    title = "JSON Editor",
    value,
    initialText,
    sample,
    onApply,
    onChangeText,
    readOnly = false,
    minRows = 16,
    maxHeight = "70vh",
    extraActions,
    storageKey,
    showStatement = false,
    initialStatement = "",
    onStatementChange,
    // schema,
    // ajvOptions,
}: JsonEditorProps<T>) {
    // const ajvRef = useRef<Ajv | null>(null);
    const firstMount = useRef(true);

    // Compute the initial text
    const initial = useMemo(() => {
        let result = "";
        if (initialText && initialText.trim().length) result = initialText;
        else if (storageKey) {
            const saved = localStorage.getItem(storageKey);
            if (saved) result = saved;
        }
        if (!result && value !== undefined) result = JSON.stringify(value, null, 2);
        if (!result && sample !== undefined) result = JSON.stringify(sample, null, 2);
        if (!result) result = "{\n  \n}";
        
        // Reorder with statement first if showStatement is enabled
        if (showStatement && result) {
            try {
                const parsed = JSON.parse(result);
                if (parsed.statement !== undefined) {
                    const reordered: any = { statement: parsed.statement };
                    Object.keys(parsed).forEach(key => {
                        if (key !== 'statement') {
                            reordered[key] = parsed[key];
                        }
                    });
                    return JSON.stringify(reordered, null, 2);
                }
            } catch (e) {
                // Invalid JSON, return as is
            }
        }
        return result;
    }, [initialText, value, sample, storageKey, showStatement]);

    const [text, setText] = useState<string>(initial);
    const [error, setError] = useState<string | null>(null);
    const [statement, setStatement] = useState<string>(initialStatement);
    const isSyncingRef = useRef(false);
    // const [schemaErrors, setSchemaErrors] = useState<ErrorObject[] | null>(null);

    // Lazy init AJV
    // useEffect(() => {
    //   if (schema && !ajvRef.current) {
    //     ajvRef.current = new Ajv(ajvOptions);
    //   }
    // }, [schema, ajvOptions]);

    // Autosave
    useEffect(() => {
        if (!storageKey) return;
        // Avoid immediately overwriting with initial on first mount
        if (firstMount.current) {
            firstMount.current = false;
            return;
        }
        localStorage.setItem(storageKey, text);
    }, [text, storageKey]);

    // Sync statement from JSON to text area
    useEffect(() => {
        if (!showStatement || isSyncingRef.current) return;
        
        try {
            const parsed = JSON.parse(text || "{}");
            if (parsed.statement !== undefined && parsed.statement !== statement) {
                isSyncingRef.current = true;
                setStatement(parsed.statement || "");
                onStatementChange?.(parsed.statement || "");
                isSyncingRef.current = false;
            }
        } catch (e) {
            // Invalid JSON, ignore
        }
    }, [text, showStatement]);

    // Sync statement from text area to JSON
    useEffect(() => {
        if (!showStatement || isSyncingRef.current) return;
        
        isSyncingRef.current = true;
        try {
            const parsed = JSON.parse(text || "{}");
            if (parsed.statement !== statement) {
                // Create new object with statement first
                const reordered: any = { statement };
                Object.keys(parsed).forEach(key => {
                    if (key !== 'statement') {
                        reordered[key] = parsed[key];
                    }
                });
                setText(JSON.stringify(reordered, null, 2));
                onStatementChange?.(statement);
            }
        } catch (e) {
            // Invalid JSON, just update callback
            onStatementChange?.(statement);
        }
        isSyncingRef.current = false;
    }, [statement, showStatement]);

    const handleFormat = () => {
        try {
            const parsed = JSON.parse(text || "{}");
            // Reorder with statement first if showStatement is enabled
            if (showStatement && parsed.statement !== undefined) {
                const reordered: any = { statement: parsed.statement };
                Object.keys(parsed).forEach(key => {
                    if (key !== 'statement') {
                        reordered[key] = parsed[key];
                    }
                });
                setText(JSON.stringify(reordered, null, 2));
            } else {
                setText(JSON.stringify(parsed, null, 2));
            }
            setError(null);
            // setSchemaErrors(null);
        } catch (e: any) {
            setError(e?.message ?? "Invalid JSON");
        }
    };

    const handleCompact = () => {
        try {
            const parsed = JSON.parse(text || "{}");
            setText(JSON.stringify(parsed));
            setError(null);
            // setSchemaErrors(null);
        } catch (e: any) {
            setError(e?.message ?? "Invalid JSON");
        }
    };

    const handleLoadSample = () => {
        if (sample === undefined) return;
        // Reorder with statement first if showStatement is enabled
        if (showStatement && typeof sample === 'object' && sample !== null && 'statement' in sample) {
            const reordered: any = { statement: (sample as any).statement };
            Object.keys(sample).forEach(key => {
                if (key !== 'statement') {
                    reordered[key] = (sample as any)[key];
                }
            });
            setText(JSON.stringify(reordered, null, 2));
        } else {
            setText(JSON.stringify(sample, null, 2));
        }
        setError(null);
        // setSchemaErrors(null);
    };

    const handleCopy = () => {
        copy(text);
        message.success("Copied to clipboard");
    };

    const handleDownload = () => {
        const blob = new Blob([text], { type: "application/json;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.download = "config.json";
        a.href = url;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleUpload = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const raw = String(reader.result || "");
                const parsed = JSON.parse(raw); // validate quick
                
                // Reorder with statement first if showStatement is enabled
                if (showStatement && parsed.statement !== undefined) {
                    const reordered: any = { statement: parsed.statement };
                    Object.keys(parsed).forEach(key => {
                        if (key !== 'statement') {
                            reordered[key] = parsed[key];
                        }
                    });
                    setText(JSON.stringify(reordered, null, 2));
                } else {
                    setText(raw);
                }
                setError(null);
                message.success("JSON loaded from file.");
            } catch (e: any) {
                setError(e?.message ?? "Invalid JSON");
                message.error("Invalid JSON file.");
            }
        };
        reader.readAsText(file);
        return false; // prevent antd from auto uploading
    };

    const handleApply = () => {
        try {
            const parsed = JSON.parse(text || "{}");

            // If you want schema validation, uncomment this block:
            // if (schema && ajvRef.current) {
            //   const validate = ajvRef.current.compile(schema);
            //   const ok = validate(parsed);
            //   if (!ok) {
            // setSchemaErrors(validate.errors || null);
            //     throw new Error("JSON does not match the provided schema.");
            //   }
            //   setSchemaErrors(null);
            // }

            setError(null);
            onApply?.(parsed);
            message.success("Applied.");
        } catch (e: any) {
            setError(e?.message ?? "Invalid JSON");
            message.error("Invalid JSON: " + (e?.message ?? "Unknown error"));
        }
    };

    return (
        <Card
            size="small"
            style={{ borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.06)" }}
        >
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <div>
                    <Title level={5} style={{ margin: 0 }}>
                        {title}
                    </Title>
                    <Text type="secondary">
                        Paste or edit JSON. Use <b>Apply</b> to emit parsed data.
                    </Text>
                </div>

                {showStatement && (
                    <div>
                        <Text strong>Statement (Énoncé):</Text>
                        <TextArea
                            value={statement}
                            readOnly={readOnly}
                            onChange={(e) => setStatement(e.target.value)}
                            autoSize={{ minRows: 3, maxRows: 8 }}
                            placeholder="Enter lesson statement here..."
                            style={{ marginTop: 8 }}
                        />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Changes here are automatically synced with the JSON
                        </Text>
                    </div>
                )}

                {error && (
                    <Alert
                        type="error"
                        showIcon
                        message="JSON error"
                        description={<code style={{ whiteSpace: "pre-wrap" }}>{error}</code>}
                    />
                )}

                {/* If using Ajv, surface schema errors elegantly */}
                {/* {schemaErrors && !!schemaErrors.length && (
          <Alert
            type="warning"
            showIcon
            message="Schema validation"
            description={
              <div style={{ fontFamily: "monospace" }}>
                {schemaErrors.map((e, i) => (
                  <div key={i}>
                    • {e.instancePath} {e.message}
                  </div>
                ))}
              </div>
            }
          />
        )} */}

                <TextArea
                    value={text}
                    readOnly={readOnly}
                    onChange={(e) => {
                        setText(e.target.value);
                        onChangeText?.(e.target.value);
                    }}
                    autoSize={{ minRows }}
                    style={{
                        fontFamily:
                            'ui-monospace, Menlo, Monaco, "Cascadia Mono", "Segoe UI Mono", Consolas, "Liberation Mono", "Courier New", monospace',
                        maxHeight,
                        overflowX: "auto",
                        overflowY: "auto",
                    }}
                    placeholder="{ }"
                />

                <Flex wrap="wrap" gap={8} align="center">
                    {sample !== undefined && (
                        <Button onClick={handleLoadSample}>Load Sample</Button>
                    )}
                    <Button onClick={handleFormat}>Format</Button>
                    <Button onClick={handleCompact}>Compact</Button>
                    <Button onClick={handleCopy}>Copy</Button>
                    <Button onClick={handleDownload}>Download</Button>
                    <Upload beforeUpload={handleUpload} multiple={false} showUploadList={false}>
                        <Tooltip title="Load JSON from .json file">
                            <Button icon={<UploadOutlined />}>Upload</Button>
                        </Tooltip>
                    </Upload>

                    <div style={{ flex: 1 }} />
                    {extraActions}
                    <Button type="primary" onClick={handleApply}>
                        Apply
                    </Button>
                </Flex>
            </Space>
        </Card>
    );
}
